import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { FlaskConical } from "lucide-react";
import "./HelpPage.css";
import fieldPositions from "../../assets/help-field-positions.svg";
import zonesEs from "../../assets/zones-es.svg";
import zonesEn from "../../assets/zones-en.svg";
import zonesPt from "../../assets/zones-pt.svg";

const TABS = ["team", "tactics", "match", "cups"];
const ZONES_IMG = { es: zonesEs, en: zonesEn, pt: zonesPt };

// Placeholder shown until a real video is recorded.
// Replace `src` prop with the video path when ready.
function VideoPlaceholder({ label, src }) {
  if (src) {
    return (
      <video className="help-video" autoPlay loop muted playsInline>
        <source src={src} type="video/mp4" />
      </video>
    );
  }
  return (
    <div className="help-video-placeholder">
      <span className="help-video-icon">▶</span>
      <span className="help-video-label">{label}</span>
    </div>
  );
}

function TacticSection() {
  const { t, i18n } = useTranslation();
  const conds = t("help.tactics.conds", { returnObjects: true });
  const acts   = t("help.tactics.acts",  { returnObjects: true });
  const zonesImg = ZONES_IMG[i18n.language] ?? zonesEn;

  return (
    <>
      <p>{t("help.tactics.intro")}</p>
      <p>{t("help.tactics.ifElse")}</p>

      <VideoPlaceholder label={t("help.tactics.video")} src="/videos/help-tactics.mp4" />

      <h3 className="help-sub-title">{t("help.tactics.quickStartTitle")}</h3>
      <p>{t("help.tactics.quickStartDesc")}</p>
      <p className="help-tip">⚠ {t("help.tactics.quickStartTip")}</p>

      <h3 className="help-sub-title">{t("help.tactics.condTitle")}</h3>
      <p>{t("help.tactics.condDesc")}</p>
      <ul className="help-list">
        {Array.isArray(conds) && conds.map((c, i) => <li key={i}>{c}</li>)}
      </ul>
      <p className="help-tip">💡 {t("help.tactics.condBallTip")}</p>

      <h3 className="help-sub-title">{t("help.tactics.actTitle")}</h3>
      <p>{t("help.tactics.actDesc")}</p>
      <ul className="help-list">
        {Array.isArray(acts) && acts.map((a, i) => (
          <li key={i}>
            {a}
            {i === 3 && (
              <div className="help-field-block">
                <p className="help-field-caption">{t("help.tactics.fieldCaption")}</p>
                <img src={fieldPositions} alt={t("help.tactics.fieldAlt")} className="help-field-img" />
              </div>
            )}
          </li>
        ))}
      </ul>

      <h3 className="help-sub-title">{t("help.tactics.callTitle")}</h3>
      <p>{t("help.tactics.callDesc")}</p>
      <p>{t("help.tactics.callDesc2")}</p>

      <h3 className="help-sub-title">{t("help.tactics.stealTitle")}</h3>
      <p>{t("help.tactics.steal")}</p>

      <p className="help-tip">⚠ {t("help.tactics.tip")}</p>

      <h3 className="help-sub-title">{t("help.tactics.zonesTitle")}</h3>
      <p>{t("help.tactics.zonesDesc")}</p>
      <img src={zonesImg} alt={t("help.tactics.zonesAlt")} className="help-zones-img" />
      <p>{t("help.tactics.zonesActions")}</p>

      <h3 className="help-sub-title">{t("help.tactics.goalTitle")}</h3>
      <p>{t("help.tactics.goalDesc")}</p>

      <h3 className="help-sub-title">{t("help.tactics.testTitle")}</h3>
      <p>
        <Trans
          i18nKey="help.tactics.testDesc"
          components={{ flask: <FlaskConical size={15} className="help-inline-icon" /> }}
        />
      </p>
      <VideoPlaceholder label={t("help.tactics.testVideo")} src="/videos/help-autotest.mp4" />

      <h3 className="help-sub-title">{t("help.tactics.inspectorTitle")}</h3>
      <p>{t("help.tactics.inspectorDesc")}</p>
      <p>{t("help.tactics.inspectorRle")}</p>
      <VideoPlaceholder label={t("help.tactics.inspectorVideo")} src="/videos/help-inspector.mp4" />
    </>
  );
}

function TeamSection() {
  const { t } = useTranslation();
  return (
    <>
      <p>{t("help.team.p1")}</p>
      <VideoPlaceholder label={t("help.team.video")} src="/videos/help-team.mp4" />
      <p>{t("help.team.p2")}</p>
      <p>{t("help.team.p3")}</p>
      <p>{t("help.team.p4")}</p>
      <p>{t("help.team.p5")}</p>
    </>
  );
}

function MatchSection() {
  const { t } = useTranslation();
  return (
    <>
      <p>{t("help.match.p1")}</p>
      <p>{t("help.match.p2")}</p>
      <p>{t("help.match.ranking")}</p>
      <p>{t("help.match.ballBounce")}</p>
      <p>{t("help.match.ballFlight")}</p>
      <VideoPlaceholder label={t("help.match.video")} />
      <p>{t("help.match.p3")}</p>
      <p>{t("help.match.energyRing")}</p>
      <p>{t("help.match.p4")}</p>
      <p>{t("help.match.p5")}</p>
    </>
  );
}

function CupsSection() {
  const { t } = useTranslation();
  return (
    <>
      <p>{t("help.cups.p1")}</p>
      <p>{t("help.cups.p2")}</p>
      <p>{t("help.cups.p3")}</p>
      <p>{t("help.cups.p4")}</p>
      <p>{t("help.cups.p5")}</p>
      <p>{t("help.cups.p6")}</p>
    </>
  );
}

const CONTENT = { tactics: TacticSection, team: TeamSection, match: MatchSection, cups: CupsSection };

export default function HelpPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("team");
  const Content = CONTENT[tab];

  return (
    <div className="help-page">
      <h1 className="help-title">{t("help.title")}</h1>
      <p className="help-intro">{t("help.intro")}</p>

      <div className="help-tabs">
        {TABS.map((key) => (
          <button
            key={key}
            className={`help-tab ${tab === key ? "help-tab--active" : ""}`}
            onClick={() => setTab(key)}
          >
            {t(`help.section.${key}`)}
          </button>
        ))}
      </div>

      <div className="help-body">
        <Content />
      </div>
    </div>
  );
}
